import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import type { AiAnswer, SearchResult } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * La faccina di CENSO: il volto dell'assistente, richiamato dal bottone in
 * basso a destra. Fa una domanda, ascolta, e — in attesa del modulo IA —
 * risponde con ciò che sa già: la ricerca nell'inventario.
 *
 * Il volto è un SVG disegnato a mano sui colori della marca: il rosso pieno
 * per il viso, il bianco per gli occhi. Gli occhi seguono il puntatore e
 * battono le palpebre; la bocca parla mentre il testo compare; quando cerca,
 * guarda in alto e pensa. Chi ha disattivato le animazioni nel sistema vede un
 * volto fermo (`prefers-reduced-motion`, in styles.css).
 */

type Stato = 'idle' | 'parla' | 'pensa' | 'ascolta'

/**
 * Il volto: uno scudo nei rossi della marca con una visiera scura e due occhi
 * calmi. Un guardiano, non una mascotte: niente sorriso, poche mosse. Gli
 * occhi seguono il puntatore di poco e battono le palpebre; quando parla,
 * sotto la visiera si muovono quattro barre di voce; quando pensa, una linea
 * rossa scansiona la visiera e gli occhi si stringono; quando ascolta, gli
 * occhi si aprono. `misura` è il lato in pixel.
 */
export function FaccinaCenso({ stato, misura = 96, tono = 'scuro' }: { stato: Stato; misura?: number; tono?: 'scuro' | 'marca' }) {
  const ref = useRef<SVGSVGElement>(null)
  const [sguardo, setSguardo] = useState({ x: 0, y: 0 })

  // Gli occhi seguono il puntatore, di poco: un volto che ti guarda, non uno
  // che rotea gli occhi. Quando pensa guardano avanti, fissi.
  useEffect(() => {
    if (stato === 'pensa') {
      setSguardo({ x: 1.6, y: 0 })
      return
    }
    function segui(e: MouseEvent) {
      const el = ref.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const dx = e.clientX - (r.left + r.width / 2)
      const dy = e.clientY - (r.top + r.height / 2)
      const d = Math.max(1, Math.hypot(dx, dy))
      const k = Math.min(1, d / 260)
      setSguardo({ x: (dx / d) * k * 3, y: (dy / d) * k * 1.2 })
    }
    window.addEventListener('mousemove', segui)
    return () => window.removeEventListener('mousemove', segui)
  }, [stato])

  const ry = stato === 'pensa' ? 2.6 : stato === 'ascolta' ? 4.6 : 4

  return (
    <svg
      ref={ref}
      className={`faccina faccina--${stato}`}
      viewBox="0 0 96 96"
      width={misura}
      height={misura}
      role="img"
      aria-label={t('CENSO, l’assistente')}
    >
      <defs>
        {/* Lo scudo è grigio scuro con il rosso negli accenti (contorno, LED,
            scansione): troppo rosso in un angolo che si vede su ogni pagina
            stanca. Il rosso pieno resta per chi lo chiede (`tono="marca"`). */}
        <linearGradient id="faccina-scudo-scuro" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#3A3A3A" />
          <stop offset="1" stopColor="#1F1F1F" />
        </linearGradient>
        <linearGradient id="faccina-scudo-marca" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8B0000" />
          <stop offset="1" stopColor="#E10600" />
        </linearGradient>
        <linearGradient id="faccina-visiera" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2A2A2A" />
          <stop offset="1" stopColor="#141414" />
        </linearGradient>
        <radialGradient id="faccina-occhio">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.7" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#FFB3B3" />
        </radialGradient>
      </defs>
      <g className="faccina__corpo">
        <path d="M48 5 L86 17 V46 C86 68 69 85 48 91 C27 85 10 68 10 46 V17 Z" fill={tono === 'marca' ? 'url(#faccina-scudo-marca)' : 'url(#faccina-scudo-scuro)'} />
        <path d="M48 12 L79 22 V46 C79 63 66 77 48 83 C30 77 17 63 17 46 V22 Z" fill="none" stroke="#E10600" strokeOpacity={tono === 'marca' ? 0.45 : 0.9} strokeWidth="1.4" />
        <rect x="18" y="35" width="60" height="20" rx="7" fill={tono === 'marca' ? 'url(#faccina-visiera)' : '#0F0F0F'} />
        {/* Gli occhi: la lente chiara, la pupilla che segue, la palpebra. */}
        <g className="faccina__occhio">
          <ellipse cx="35" cy="45" rx="8.5" ry={ry} fill="url(#faccina-occhio)" />
          <circle cx={35 + sguardo.x} cy={45 + sguardo.y} r="2" fill="#1F1F1F" />
          <rect className="faccina__palpebra" x="25" y="39" width="20" height="12" rx="5" fill={tono === 'marca' ? 'url(#faccina-visiera)' : '#0F0F0F'} />
        </g>
        <g className="faccina__occhio">
          <ellipse cx="61" cy="45" rx="8.5" ry={ry} fill="url(#faccina-occhio)" />
          <circle cx={61 + sguardo.x} cy={45 + sguardo.y} r="2" fill="#1F1F1F" />
          <rect className="faccina__palpebra" x="51" y="39" width="20" height="12" rx="5" fill={tono === 'marca' ? 'url(#faccina-visiera)' : '#0F0F0F'} />
        </g>
        {stato === 'pensa' && <rect className="faccina__scansione" x="20" y="36" width="2" height="18" fill="#FF4D4D" fillOpacity="0.85" />}
        {stato === 'parla' ? (
          <g className="faccina__voce">
            <rect x="38" y="58" width="3" height="14" rx="1.5" fill="#FFFFFF" />
            <rect x="43.5" y="58" width="3" height="14" rx="1.5" fill="#FFFFFF" />
            <rect x="49" y="58" width="3" height="14" rx="1.5" fill="#FFFFFF" />
            <rect x="54.5" y="58" width="3" height="14" rx="1.5" fill="#FFFFFF" />
          </g>
        ) : (
          <rect className="faccina__led" x="41" y="64" width="14" height="2.2" rx="1.1" fill="#FF4D4D" fillOpacity="0.8" />
        )}
      </g>
    </svg>
  )
}

/** Il testo compare una lettera alla volta; `onFine` quando ha finito. */
function useMacchinaDaScrivere(testo: string, attivo: boolean, onFine: () => void) {
  const [mostrato, setMostrato] = useState('')
  useEffect(() => {
    if (!attivo) return
    setMostrato('')
    let i = 0
    const id = setInterval(() => {
      i += 1
      setMostrato(testo.slice(0, i))
      if (i >= testo.length) {
        clearInterval(id)
        onFine()
      }
    }, 22)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testo, attivo])
  return mostrato
}

/** La domanda con cui la faccina apre, a seconda di dove si è. */
function domandaPer(pathname: string): string {
  if (pathname.startsWith('/cis')) return t('Ciao, sono CENSO. Cerchi un apparato, un indirizzo o un seriale?')
  if (pathname.startsWith('/eagle')) return t('Ciao, sono CENSO. Vuoi sapere cosa non sta rispondendo?')
  if (pathname.startsWith('/security') || pathname.startsWith('/vuln')) return t('Ciao, sono CENSO. Cerchi un rischio, una vulnerabilità o una macchina esposta?')
  return t('Ciao, sono CENSO. Cosa vuoi sapere del tuo inventario?')
}

/** Le domande suggerite: parole che la ricerca capisce già oggi. */
function suggerimentiPer(pathname: string): string[] {
  if (pathname.startsWith('/eagle')) return [t('sonde'), t('firewall'), t('switch')]
  return [t('windows server'), t('firewall'), t('switch'), t('uplink')]
}

const LATO_FAB = 60
const CHIAVE_POSIZIONE = 'censo.assistente.posizione'

type Posizione = { x: number; y: number }

/**
 * Dove sta il bottone: in basso a destra finché non lo si trascina, poi dove
 * lo si è lasciato, ricordato in questo browser. Un angolo fisso dà fastidio
 * a qualcuno su qualche pagina, e la soluzione è lasciarlo spostare, non
 * indovinare l'angolo giusto per tutti.
 */
function usePosizione(): [Posizione | null, (p: Posizione | null) => void] {
  const [pos, setPos] = useState<Posizione | null>(() => {
    try {
      const grezzo = localStorage.getItem(CHIAVE_POSIZIONE)
      if (!grezzo) return null
      const p = JSON.parse(grezzo) as Posizione
      return Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null
    } catch {
      return null
    }
  })
  function salva(p: Posizione | null) {
    setPos(p)
    try {
      if (p) localStorage.setItem(CHIAVE_POSIZIONE, JSON.stringify(p))
      else localStorage.removeItem(CHIAVE_POSIZIONE)
    } catch {
      /* senza memoria del browser la posizione vale per questa pagina */
    }
  }
  return [pos, salva]
}

function dentroLoSchermo(p: Posizione): Posizione {
  return {
    x: Math.min(Math.max(8, p.x), window.innerWidth - LATO_FAB - 8),
    y: Math.min(Math.max(8, p.y), window.innerHeight - LATO_FAB - 8),
  }
}

/** Il punto in cui il bottone sta davvero, anche quando è nell'angolo di default. */
function posizioneEffettiva(pos: Posizione | null): Posizione {
  return pos ?? { x: window.innerWidth - LATO_FAB - 22, y: window.innerHeight - LATO_FAB - 22 }
}

/**
 * Trascinare con il puntatore: sotto i 4 px è un clic (`onClic`), oltre è uno
 * spostamento (`onSposta` a ogni mossa, `onFine` al rilascio).
 */
function trascinabile(
  origine: () => Posizione,
  onSposta: (p: Posizione) => void,
  onFine: (spostato: boolean) => void,
) {
  return (e: React.PointerEvent) => {
    if (e.button !== 0) return
    // Un clic su un pulsante o un campo **dentro** l'area trascinabile è un
    // clic su quello: la × non chiudeva perché la testa catturava il
    // puntatore. Ma il bottone della faccina è esso stesso un pulsante, e la
    // prima versione ignorava anche lui: si esclude solo ciò che sta dentro e
    // non è l'elemento che porta il gestore.
    const dentro = (e.target as HTMLElement).closest('button, a, input, select, textarea')
    if (dentro && dentro !== e.currentTarget) return
    const partenza = { px: e.clientX, py: e.clientY, ...origine() }
    let spostato = false
    const el = e.currentTarget as HTMLElement
    el.setPointerCapture(e.pointerId)
    function muovi(ev: PointerEvent) {
      const dx = ev.clientX - partenza.px
      const dy = ev.clientY - partenza.py
      if (!spostato && Math.hypot(dx, dy) < 4) return
      spostato = true
      onSposta(dentroLoSchermo({ x: partenza.x + dx, y: partenza.y + dy }))
    }
    function fine() {
      el.removeEventListener('pointermove', muovi)
      el.removeEventListener('pointerup', fine)
      el.removeEventListener('pointercancel', fine)
      onFine(spostato)
    }
    el.addEventListener('pointermove', muovi)
    el.addEventListener('pointerup', fine)
    el.addEventListener('pointercancel', fine)
  }
}

export default function Assistente() {
  const [aperto, setAperto] = useState(false)
  const [pos, salvaPos] = usePosizione()
  const [bozza, setBozza] = useState<Posizione | null>(null)
  const bozzaRef = useRef<Posizione | null>(null)
  bozzaRef.current = bozza
  const mostrata = bozza ?? pos
  const [stato, setStato] = useState<Stato>('idle')
  const [q, setQ] = useState('')
  const [dati, setDati] = useState<SearchResult | null>(null)
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { principal } = useAuth()
  const domanda = domandaPer(pathname)
  const scritto = useMacchinaDaScrivere(domanda, aperto, () => setStato('ascolta'))
  // Con il modulo IA acceso e un modello configurato, la domanda va al
  // modello (POST /ai/ask); altrimenti resta la ricerca nell'inventario.
  const ia = useAsync(() => (aperto ? api.aiStatus() : Promise.resolve(null)), [aperto])
  const conIA = ia.data?.available === true
  const [chat, setChat] = useState<Messaggio[]>([])
  const [pensando, setPensando] = useState(false)
  const chatRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (aperto) {
      setStato('parla')
      setQ('')
      setDati(null)
    } else {
      setStato('idle')
      setPensando(false)
    }
  }, [aperto])

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight })
  }, [chat, pensando])

  async function invia() {
    const testo = q.trim()
    if (!testo || pensando) return
    const storia = chat.slice(-12).map((m) => ({ role: m.ruolo, text: m.testo }))
    setChat((c) => [...c, { ruolo: 'user', testo }])
    setQ('')
    setPensando(true)
    setStato('pensa')
    try {
      const r = await api.aiAsk(testo, storia)
      setChat((c) => [...c, rispostaDa(r)])
      setStato('parla')
      setTimeout(() => setStato('ascolta'), 1200)
    } catch (e) {
      setChat((c) => [...c, { ruolo: 'assistant', testo: e instanceof Error ? e.message : String(e) }])
      setStato('ascolta')
    } finally {
      setPensando(false)
      inputRef.current?.focus()
    }
  }

  useEffect(() => {
    if (stato === 'ascolta') inputRef.current?.focus()
  }, [stato])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setAperto(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // In attesa del modulo IA, la risposta è la ricerca nell'inventario: la
  // stessa della lente, con la faccina che «pensa» mentre aspetta.
  useEffect(() => {
    if (!aperto || conIA) return
    const testo = q.trim()
    if (testo.length < 2) {
      setDati(null)
      setBusy(false)
      return
    }
    let vivo = true
    setBusy(true)
    setStato('pensa')
    const timer = setTimeout(() => {
      api.ricercaGlobale(testo)
        .then((r) => { if (vivo) setDati(r) })
        .catch(() => { if (vivo) setDati({ query: testo, groups: [] }) })
        .finally(() => { if (vivo) { setBusy(false); setStato('ascolta') } })
    }, 300)
    return () => { vivo = false; clearTimeout(timer) }
  }, [q, aperto, conIA])

  function vai(to: string) {
    setAperto(false)
    navigate(to)
  }

  // Il bottone e il pannello stanno dove l'utente li ha messi: il pannello
  // si appende al bordo più vicino al bottone, così non esce dallo schermo.
  const stileFab: React.CSSProperties | undefined = mostrata
    ? { left: mostrata.x, top: mostrata.y, right: 'auto', bottom: 'auto' }
    : undefined
  const stilePannello: React.CSSProperties | undefined = (() => {
    if (!mostrata) return undefined
    const aDestra = mostrata.x + LATO_FAB / 2 > window.innerWidth / 2
    const inBasso = mostrata.y + LATO_FAB / 2 > window.innerHeight / 2
    return {
      left: aDestra ? 'auto' : mostrata.x,
      right: aDestra ? window.innerWidth - mostrata.x - LATO_FAB : 'auto',
      top: inBasso ? 'auto' : mostrata.y,
      bottom: inBasso ? window.innerHeight - mostrata.y - LATO_FAB : 'auto',
    }
  })()
  const trascina = trascinabile(
    () => posizioneEffettiva(pos),
    setBozza,
    (spostato) => {
      if (spostato && bozzaRef.current) salvaPos(bozzaRef.current)
      else if (!spostato && !aperto) setAperto(true)
      setBozza(null)
    },
  )

  if (!aperto) {
    return (
      <button
        type="button"
        className={`assistente__fab${bozza ? ' assistente__fab--in-mano' : ''}`}
        style={stileFab}
        onPointerDown={trascina}
        title={t('Chiedi a CENSO (trascina per spostarlo)')}
        aria-label={t('Chiedi a CENSO')}
      >
        <FaccinaCenso stato="idle" misura={44} />
      </button>
    )
  }

  const gruppi = dati?.groups ?? []
  const vuoto = !busy && q.trim().length >= 2 && gruppi.length === 0
  const totale = gruppi.reduce((n, g) => n + g.hits.length, 0)
  const isAdmin = principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin')
  const fumetto = conIA
    ? pensando
      ? t('Ci sto guardando…')
      : chat.length
        ? t('Ecco. Vuoi sapere altro?')
        : scritto
    : q.trim().length >= 2
      ? busy
        ? t('Un attimo, guardo nell’inventario…')
        : vuoto
          ? t('Non trovo niente con «{q}». Prova con un nome, un indirizzo, un MAC o un seriale.', { q: q.trim() })
          : t('Ho trovato {n} cose. Scegli, o scrivi ancora.', { n: totale })
      : scritto

  return (
    <div className="assistente" role="dialog" aria-label={t('Chiedi a CENSO')} style={stilePannello}>
      <div className="assistente__testa" onPointerDown={trascina} title={t('Trascina per spostare')}>
        <FaccinaCenso stato={stato} misura={88} />
        <div className="assistente__fumetto">{fumetto}</div>
        <button type="button" className="assistente__chiudi" onClick={() => setAperto(false)} aria-label={t('Chiudi')}>
          ×
        </button>
      </div>

      <div className="assistente__corpo">
        {conIA && chat.length > 0 && (
          <div className="assistente__chat" ref={chatRef}>
            {chat.map((m, i) => (
              <div key={i} className={`assistente__msg assistente__msg--${m.ruolo}`}>
                <div className="assistente__msg-testo">{conLink(m.testo, vai)}</div>
                {m.passi && m.passi.length > 0 && (
                  <div className="assistente__passi">
                    {t('Ho guardato: {cosa}', { cosa: m.passi.map((p) => p.tool).join(', ') })}
                  </div>
                )}
                {m.link && m.link.length > 0 && (
                  <div className="assistente__chips">
                    {m.link.map((l) => (
                      <button key={l.to} type="button" className="assistente__chip" onClick={() => vai(l.to)}>
                        {l.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {pensando && <div className="assistente__msg assistente__msg--assistant assistente__msg--attesa">…</div>}
          </div>
        )}
        <input
          ref={inputRef}
          className="assistente__input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => stato !== 'parla' && stato !== 'pensa' && setStato('ascolta')}
          onKeyDown={(e) => {
            if (conIA && e.key === 'Enter') {
              e.preventDefault()
              void invia()
            }
          }}
          placeholder={conIA ? t('Fai una domanda sul tuo inventario…') : t('Scrivi qui…')}
          aria-label={t('La tua domanda')}
          disabled={pensando}
        />
        {q.trim().length < 2 && chat.length === 0 && (
          <div className="assistente__chips">
            {(conIA ? domandeSuggerite(pathname) : suggerimentiPer(pathname)).map((s) => (
              <button key={s} type="button" className="assistente__chip" onClick={() => setQ(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
        {!conIA && gruppi.length > 0 && (
          <div className="assistente__risultati">
            {gruppi.map((g) => (
              <div key={g.kind} className="assistente__gruppo">
                <div className="assistente__gruppo-titolo">{t(g.label)}</div>
                {g.hits.map((h) => (
                  <button key={h.id} type="button" className="assistente__voce" onClick={() => vai(h.to)}>
                    <span className="assistente__voce-titolo">{h.title}</span>
                    {h.subtitle && <span className="assistente__voce-sotto">{h.subtitle}</span>}
                  </button>
                ))}
                {g.more && <div className="assistente__gruppo-altro">{t('… e altri: apri l’elenco per vederli tutti')}</div>}
              </div>
            ))}
          </div>
        )}
        <p className="assistente__nota">
          {conIA
            ? t('Rispondo con ciò che leggo nel tuo inventario, con i link per verificare. Modello: {m}.', { m: ia.data?.credential_name ?? '' })
            : ia.data?.reason === 'not_configured'
              ? isAdmin
                ? t('Per ora rispondo cercando nell’inventario. Per le domande in italiano aggiungi una credenziale «Claude» o «Modello interno» fra le credenziali.')
                : t('Per ora rispondo cercando nell’inventario. Per le domande in italiano serve che chi amministra il tenant configuri un modello.')
              : ia.data?.reason === 'not_licensed'
                ? t('Per ora rispondo cercando nell’inventario. Le domande in italiano sono nel modulo aggiuntivo «Assistente».')
                : t('Per ora rispondo cercando nell’inventario. Con il modulo IA capirò le domande in italiano.')}
        </p>
      </div>
    </div>
  )
}

/** Un messaggio della conversazione con l'assistente. */
type Messaggio = {
  ruolo: 'user' | 'assistant'
  testo: string
  passi?: { tool: string }[]
  link?: { title: string; to: string }[]
}

/** La risposta del Control Plane come messaggio, anche quando non ha potuto rispondere. */
function rispostaDa(r: AiAnswer): Messaggio {
  if (!r.available) {
    const perche: Record<string, string> = {
      not_licensed: t('Non compreso nella licenza'),
      not_configured: t('Nessun modello configurato: serve una credenziale «Claude» o «Modello interno».'),
      quota_exceeded: t('La quota di oggi è finita: riprova domani, o alza la quota nelle impostazioni.'),
      provider_error: t('Il modello non ha risposto: {perche}', { perche: r.reason ?? '' }),
    }
    return { ruolo: 'assistant', testo: perche[r.code ?? ''] ?? (r.reason || t('Non posso rispondere adesso.')) }
  }
  return { ruolo: 'assistant', testo: r.answer ?? '', passi: r.steps, link: r.links }
}

/**
 * Il testo della risposta con i link cliccabili: il modello scrive
 * «[nome](/cis/…)» come glielo chiede il prompt, e qui diventa un link della
 * console. Tutto il resto resta testo: niente HTML dal modello.
 */
function conLink(testo: string, vai: (to: string) => void) {
  const parti: React.ReactNode[] = []
  const re = /\[([^\]]+)\]\((\/[^)\s]+)\)/g
  let ultimo = 0
  let m: RegExpExecArray | null
  let k = 0
  while ((m = re.exec(testo)) !== null) {
    if (m.index > ultimo) parti.push(testo.slice(ultimo, m.index))
    const to = m[2]
    parti.push(
      <a key={k++} href={to} onClick={(e) => { e.preventDefault(); vai(to) }}>
        {m[1]}
      </a>,
    )
    ultimo = m.index + m[0].length
  }
  if (ultimo < testo.length) parti.push(testo.slice(ultimo))
  return parti
}

/** Le domande suggerite quando risponde il modello. */
function domandeSuggerite(pathname: string): string[] {
  if (pathname.startsWith('/eagle')) return [t('Cosa non sta rispondendo adesso?'), t('Quali CI hanno la CPU in attenzione?')]
  if (pathname.startsWith('/security')) return [t('Quali rischi critici ho aperti?'), t('Cosa è esposto su Internet?')]
  return [
    t('Quanti server Windows ho e quali?'),
    t('Cosa stacco se spengo il core?'),
    t('Quali contratti scadono entro 90 giorni?'),
    t('Quanto mi costa il cloud questo mese?'),
  ]
}
